import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { logoSvg } from "../../utilities/Constants";
import { Observable, Subject, takeUntil } from "rxjs";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ColumnState, GridApi } from "ag-grid-community";
import { HyphenatedPipe } from "../../pipes/HyphenatedPipe";

@Component({
  standalone: false,
  selector: 'mm-print-friendly-modal',
  templateUrl: './print-friendly-modal.component.html',
  styleUrls: ['./print-friendly-modal.component.scss']
})

export class PrintFriendlyModalComponent implements OnInit {
  @Input() showModal: Observable<boolean>;
  @Input() tableToPrint: string;
  @Input() accountNumber: string;
  @Input() gridApi: GridApi | undefined | null;
  @Input() gridColumnApi: GridApi | undefined | null;
  @Input() masterTableName: string;

  @Output() printButtonClicked = new EventEmitter<boolean>();

  destroy$: Subject<boolean> = new Subject<boolean>();
  modalId: string;
  modal: EverestModal;
  isPrintConfirmClickedOrCanceled: boolean = false;
  svg: SafeHtml;
  columnHeaders: string[];
  rows: string[];
  tableName: string;
  sortedMasterRows: string[] = [];
  gridTemplateColumns: string;

  constructor(private sanitizer: DomSanitizer) { }

  ngOnInit(): void {
    this.svg = this.sanitizer.bypassSecurityTrustHtml(logoSvg);
    this.modalId = 'print-friendly-modal-' + new Date().getTime().toString();
  }

  ngAfterViewInit(): void {
    const hyphenatedPipe = new HyphenatedPipe();
    this.modal = document.getElementById(this.modalId) as EverestModal;
    this.modal.addEventListener('closeButtonClick', () => {
    });
    this.modal.addEventListener('close', () => {
      if (!this.isPrintConfirmClickedOrCanceled) {
        this.onCancelClicked();
      }
    });
    this.showModal.pipe(takeUntil(this.destroy$)).subscribe((show) => {
      this.accountNumber = hyphenatedPipe.transform(this.accountNumber);
      if (show) {
        this.modal.showModal();
        this.modal.focus();
        this.isPrintConfirmClickedOrCanceled = false;
        this.getTableHtmlData();
      }
    });
  }
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onCancelClicked(): void {
    document?.getElementById('table-header')?.scrollIntoView();
    this.isPrintConfirmClickedOrCanceled = true;
    this.hideModal();
  }

  onPrintButtonClicked(): void {
    this.isPrintConfirmClickedOrCanceled = true;
    this.printButtonClicked.emit(true);
  }

  getTableHtmlData(): void {
    this.columnHeaders = [];
    this.rows = [];
    this.tableName = this.masterTableName === 'Status' ? 'Status' : 'Recurring';
    let rowData: any[] = [];
    this.gridApi?.forEachNodeAfterFilter(node => rowData.push(node.data));
    let columnSortPreference = this.gridColumnApi?.getColumnState().find(preference => preference.sort);
    this.sortedMasterRows = getAllSortedRows(rowData, columnSortPreference);
    let columnsInOrder = this.gridColumnApi?.getColumnState().map(x => x.colId) ?? [];

    columnsInOrder?.filter(x => x !== 'Actions').forEach((col: any) => {
      this.columnHeaders.push(col);
    })
    this.gridTemplateColumns = new Array(this.columnHeaders.length).fill('auto').join(' ');
    this.sortedMasterRows?.map(row => {
      columnsInOrder?.filter(x => x !== 'Actions' && x).forEach((col: any) => {
        this.rows.push(row[col ?? ''] ?? '');
      })
    })
  }

  hideModal() {
    this.modal.hideModal(); //in separate function in order to mock it in unit tests
  }
}

type EverestModal = HTMLElement & {
  showModal: () => {},
  hideModal: () => {}
};
export function getAllSortedRows(rowData: any[], columnSortPreference: ColumnState | undefined): string[] {
  // Sort rows based on column sort preference, colId is the column being sorted on
  if (!rowData || rowData.length <= 1 || !columnSortPreference?.colId) {
    return rowData;
  }
  return [...rowData].sort((a, b) => {
    // assign placeholder values to handle special sorting cases
    var n1 = a[columnSortPreference.colId];
    var n2 = b[columnSortPreference.colId];
    if (columnSortPreference.colId === 'Process date' || columnSortPreference.colId === 'Next process date') {
      // agGrid sort evaluates 'N/A' < '04/25/2025' (any date formatted as such) to true
      // Typescript evaluates 'N/A' > '04/25/2025' to true
      // When date is 'N/A', set to the lowest date possible for getTime() method to flip the evaluation
      if (n1 === 'N/A'){
        n1 = '01/01/1970';
      }
      if (n2 === 'N/A'){
        n2 = '01/01/1970';
      }
      // convert to milliseconds since 01/01/1970 for numerical comparison
      n1 = new Date(n1).getTime();
      n2 = new Date(n2).getTime();
    }
    if (columnSortPreference.colId === 'Amount' || columnSortPreference.colId === 'Available cash'){
      // agGrid appears to be sorting currency values by their absolute values
      // format currency values to do that comparison here
      n1 = Number(n1.replace(/[^0-9.-]+/g,""));
      n2 = Number(n2.replace(/[^0-9.-]+/g,""));
      n1 = Math.abs(n1);
      n2 = Math.abs(n2);
    }
    if (n1 < n2) {
      return columnSortPreference.sort === 'desc' ? 1 : -1;
    } else if (n1 > n2) {
      return columnSortPreference.sort === 'asc' ? 1 : -1;
    } else {
      return 0;
    }
  });
}
